import type {
  Analysis,
  CodeMove,
  ComparisonIndex,
  DiffLine,
  FileDiff,
  FileStatus,
  TextSecurity,
  TextSegment,
} from "../types.js";

export const palette = [
  "#e4562f",
  "#267a68",
  "#7657c8",
  "#c58a17",
  "#2874a8",
  "#b34775",
];

export function moveColor(id: number | string): string {
  return palette[(Number(id) - 1) % palette.length];
}

export function moveReviewId(id: number | string): string {
  return `move:${id}`;
}

export function shortPath(path: string): string {
  const parts = path.split("/");
  return parts.at(-1) || path;
}

export function shortestUniquePaths(paths: string[]): Map<string, string> {
  const uniquePaths = [...new Set(paths)];
  const partsByPath = new Map(uniquePaths.map((path) => [path, path.split("/").filter(Boolean)]));
  return new Map(uniquePaths.map((path) => {
    const parts = partsByPath.get(path)!;
    for (let length = 1; length <= parts.length; length += 1) {
      const suffix = parts.slice(-length).join("/");
      const collision = uniquePaths.some((otherPath) =>
        otherPath !== path && partsByPath.get(otherPath)!.slice(-length).join("/") === suffix
      );
      if (!collision) return [path, suffix];
    }
    return [path, parts.join("/") || path];
  }));
}

export function splitDisplayPath(path: string): { directory: string; filename: string } {
  const parts = path.split("/");
  return {
    directory: parts.slice(0, -1).join("/"),
    filename: parts.at(-1) || path,
  };
}

export function directoryPathParts(
  fullPath: string,
  abbreviatedPath: string,
): { shared: string; distinguishing: string } {
  const fullDirectory = splitDisplayPath(fullPath).directory;
  const abbreviatedDirectory = splitDisplayPath(abbreviatedPath).directory;
  if (!fullDirectory) return { shared: "", distinguishing: "" };
  if (!abbreviatedDirectory || !fullDirectory.endsWith(abbreviatedDirectory)) {
    return { shared: `${fullDirectory}/`, distinguishing: "" };
  }
  return {
    shared: fullDirectory.slice(0, -abbreviatedDirectory.length),
    distinguishing: `${abbreviatedDirectory}/`,
  };
}

export function statusLetter(status: FileStatus): string {
  return { added: "A", deleted: "D", renamed: "R", modified: "M" }[status] || "M";
}

export function fileIcon(path: string): string {
  const extension = path.split(".").at(-1);
  const icons: Record<string, string> = {
    rs: "Rs",
    js: "JS",
    ts: "TS",
    tsx: "TX",
    py: "Py",
    go: "Go",
    css: "Cs",
    html: "Ht",
  };
  return extension ? icons[extension] || "·" : "·";
}

export function fileCounts(file: FileDiff): { added: number; deleted: number } {
  const lines = file.hunks.flatMap((hunk) => hunk.lines);
  return {
    added: lines.filter((line) => line.kind === "addition").length,
    deleted: lines.filter((line) => line.kind === "deletion").length,
  };
}

export function visualColumns(text: string, tabSize = 8): number {
  let columns = 0;
  for (const character of text) {
    if (character === "\t") columns += tabSize - (columns % tabSize);
    else columns += 1;
  }
  return columns;
}

export function securityWarningCount(security: TextSecurity | null | undefined): number {
  if (!security) return 0;
  return (
    security.bidiControls +
    security.invisibleCharacters +
    security.controlCharacters
  );
}

export interface EditLineGroup {
  additions: DiffLine[];
  deletions: DiffLine[];
  pairs: Array<{ addition: DiffLine; deletion: DiffLine }>;
}

export interface SplitDiffRow {
  alignedEdit: boolean;
  line: DiffLine;
  newLine: DiffLine | null;
  oldLine: DiffLine | null;
}

export function indexEditLineGroups(lines: DiffLine[]): Map<number, EditLineGroup> {
  const groups = new Map<number, EditLineGroup>();
  lines.forEach((line) => {
    if (line.editId == null || line.moveId != null) return;
    let group = groups.get(line.editId);
    if (!group) {
      group = { additions: [], deletions: [], pairs: [] };
      groups.set(line.editId, group);
    }
    if (line.kind === "addition") group.additions.push(line);
    if (line.kind === "deletion") group.deletions.push(line);
  });
  groups.forEach((group) => {
    const deletions = group.deletions.filter((line) => line.editPair);
    const additions = group.additions.filter((line) => line.editPair);
    group.pairs = deletions
      .slice(0, additions.length)
      .map((deletion, index) => ({ addition: additions[index], deletion }));
  });

  // Semantic pairs become split-layout anchors. Keep only a non-crossing set:
  // additions are already visited in new-side order, so accepted deletions must
  // also advance in old-side order.
  const lineIndexes = new Map(lines.map((line, index) => [line, index]));
  const pairCandidates = new Map<DiffLine, {
    group: EditLineGroup;
    pair: { addition: DiffLine; deletion: DiffLine };
  }>();
  groups.forEach((group) => {
    group.pairs.forEach((pair) => pairCandidates.set(pair.addition, { group, pair }));
    group.pairs = [];
  });

  let lastDeletionIndex = -1;
  lines.forEach((line) => {
    if (line.kind !== "addition") return;

    const candidate = pairCandidates.get(line);
    const deletionIndex = candidate ? lineIndexes.get(candidate.pair.deletion) : undefined;
    if (candidate && deletionIndex != null && deletionIndex > lastDeletionIndex) {
      candidate.group.pairs.push(candidate.pair);
      lastDeletionIndex = deletionIndex;
    }
  });
  return groups;
}

export function layoutSplitDiffRows(lines: DiffLine[]): SplitDiffRow[] {
  const editGroups = indexEditLineGroups(lines);
  const rows: SplitDiffRow[] = [];
  let ordinaryRun: DiffLine[] = [];

  const appendRow = (
    oldLine: DiffLine | null,
    newLine: DiffLine | null,
    alignedEdit = false,
  ) => {
    const line = oldLine ?? newLine;
    if (line) rows.push({ alignedEdit, line, newLine, oldLine });
  };

  const appendUnmatched = (oldLines: DiffLine[], newLines: DiffLine[]) => {
    const rowCount = Math.max(oldLines.length, newLines.length);
    for (let index = 0; index < rowCount; index += 1) {
      appendRow(oldLines[index] ?? null, newLines[index] ?? null);
    }
  };

  const flushOrdinaryRun = () => {
    if (!ordinaryRun.length) return;
    const oldLines = ordinaryRun.filter((line) => line.kind === "deletion");
    const newLines = ordinaryRun.filter((line) => line.kind === "addition");
    const oldIndexes = new Map(oldLines.map((line, index) => [line, index]));
    const newIndexes = new Map(newLines.map((line, index) => [line, index]));
    const anchors = [...editGroups.values()]
      .flatMap((group) => group.pairs)
      .filter((pair) => oldIndexes.has(pair.deletion) && newIndexes.has(pair.addition))
      .sort((left, right) => oldIndexes.get(left.deletion)! - oldIndexes.get(right.deletion)!);

    let oldStart = 0;
    let newStart = 0;
    for (const anchor of anchors) {
      const oldEnd = oldIndexes.get(anchor.deletion)!;
      const newEnd = newIndexes.get(anchor.addition)!;
      appendUnmatched(oldLines.slice(oldStart, oldEnd), newLines.slice(newStart, newEnd));
      appendRow(anchor.deletion, anchor.addition, true);
      oldStart = oldEnd + 1;
      newStart = newEnd + 1;
    }
    appendUnmatched(oldLines.slice(oldStart), newLines.slice(newStart));
    ordinaryRun = [];
  };

  for (const line of lines) {
    const ordinaryChange =
      (line.kind === "addition" || line.kind === "deletion") && line.moveId == null;
    if (ordinaryChange) {
      ordinaryRun.push(line);
      continue;
    }

    flushOrdinaryRun();
    if (line.kind === "context") appendRow(line, line);
    else if (line.kind === "deletion") appendRow(line, null);
    else if (line.kind === "addition") appendRow(null, line);
    else rows.push({ alignedEdit: false, line, newLine: null, oldLine: null });
  }
  flushOrdinaryRun();
  return rows;
}

export function collapsibleMoveComparisons(move: CodeMove, context = 2): Set<string> {
  const alignments = [...new Set(move.comparison.map((line) => line.alignmentId))];
  const changedIndexes = alignments
    .map((alignment, index) => move.comparison.some((line) =>
      line.alignmentId === alignment && line.kind !== "unchanged",
    ) ? index : -1)
    .filter((index) => index >= 0);
  const collapsed = new Set<string>();
  alignments.forEach((alignment, index) => {
    const nearEdge = index < context || index >= alignments.length - context;
    const nearChange = changedIndexes.some((changed) => Math.abs(changed - index) <= context);
    if (!nearEdge && !nearChange) collapsed.add(`${move.id}:${alignment}`);
  });
  return collapsed;
}

export function indexComparisons(analysis: Analysis): ComparisonIndex {
  const collapsedComparisonIds = new Set<string>();
  const collapsedLineIds = new Set<string>();
  const wordChanges = new Map<string, TextSegment[]>();
  const lineComparisons = new Map<string, string>();
  const realLocations = new Map<string, { rangeKey: string }>();
  analysis.moves.forEach((move) => {
    collapsibleMoveComparisons(move).forEach((id) => collapsedComparisonIds.add(id));
    move.comparison.forEach((line) => {
      const comparisonId = `${move.id}:${line.alignmentId}`;
      if (line.sourceNumber != null) {
        const key = `${move.id}:source:${line.sourceNumber}`;
        wordChanges.set(key, line.source);
        lineComparisons.set(key, comparisonId);
      }
      if (line.targetNumber != null) {
        const key = `${move.id}:target:${line.targetNumber}`;
        wordChanges.set(key, line.target);
        lineComparisons.set(key, comparisonId);
      }
    });
  });
  analysis.files.forEach((file, fileIndex) => {
    file.hunks.forEach((hunk, hunkIndex) => {
      hunk.lines.forEach((line) => {
        if (!line.moveId || !line.moveRole) return;
        const lineNumber = line.moveRole === "source" ? line.oldNumber : line.newNumber;
        const comparisonId = lineComparisons.get(`${line.moveId}:${line.moveRole}:${lineNumber}`);
        if (comparisonId) {
          if (collapsedComparisonIds.has(comparisonId)) collapsedLineIds.add(line.id);
          realLocations.set(`${line.moveRole}:${comparisonId}`, {
            rangeKey: `${fileIndex}:${hunkIndex}`,
          });
        }
      });
    });
  });
  return { collapsedComparisonIds, collapsedLineIds, lineComparisons, realLocations, wordChanges };
}

export function collectReviewIds(
  analysis: Analysis,
  reviewIdsForHunk: (lines: DiffLine[], fileIndex: number, hunkIndex: number) => Array<string | null>,
): Set<string> {
  const ids = new Set<string>();
  analysis.files.forEach((file, fileIndex) => {
    file.hunks.forEach((hunk, hunkIndex) => {
      reviewIdsForHunk(hunk.lines, fileIndex, hunkIndex).forEach((id) => {
        if (id) ids.add(id);
      });
    });
  });
  return ids;
}
